import React, { useState } from 'react';
import { 
  BookOpen, 
  Users, 
  Building, 
  GraduationCap, 
  ChevronRight, 
  ExternalLink,
  Search,
  Filter
} from 'lucide-react';

const DepartmentsSection = () => {
  const [activeTab, setActiveTab] = useState('departments');
  const [searchTerm, setSearchTerm] = useState('');

  const departments = [
    { name: 'Applied Mechanics', url: 'https://am.iitd.ac.in/' },
    { name: 'Biochemical Engineering and Biotechnology', url: 'https://beb.iitd.ac.in/' },
    { name: 'Chemical Engineering', url: 'http://chemical.iitd.ac.in/' },
    { name: 'Chemistry', url: 'https://chemistry.iitd.ac.in/' },
    { name: 'Civil & Environmental Engineering', url: 'https://civil.iitd.ac.in/' },
    { name: 'Computer Science and Engineering', url: 'https://www.cse.iitd.ac.in/' },
    { name: 'Design', url: 'https://design.iitd.ac.in/' },
    { name: 'Electrical Engineering', url: 'https://ee.iitd.ac.in/' },
    { name: 'Energy Science and Engineering', url: 'https://dese.iitd.ac.in/' },
    { name: 'Humanities and Social Sciences', url: 'https://hss.iitd.ac.in/' },
    { name: 'Management Studies', url: 'https://dms.iitd.ac.in/' },
    { name: 'Materials Science and Engineering', url: 'https://mse.iitd.ac.in/' },
    { name: 'Mathematics', url: 'https://maths.iitd.ac.in/' },
    { name: 'Mechanical Engineering', url: 'https://mech.iitd.ac.in/' },
    { name: 'Physics', url: 'https://physics.iitd.ac.in/' },
    { name: 'Textile and Fibre Engineering', url: 'https://textile.iitd.ac.in/' }
  ];

  const centers = [
    { name: 'Applied Research in Electronics', url: 'https://care.iitd.ac.in/' },
    { name: 'Atmospheric Sciences', url: 'https://cas.iitd.ac.in/' },
    { name: 'Automotive Research and Tribology', url: 'https://cart.iitd.ac.in/' },
    { name: 'Biomedical Engineering', url: 'https://cbme.iitd.ac.in/' },
    { name: 'Computer Services Centre', url: 'https://csc.iitd.ac.in/' },
    { name: 'Educational Technology', url: 'https://etsc.iitd.ac.in/' },
    { name: 'Transportation Research and Injury Prevention', url: 'https://tripp.iitd.ac.in/' },
    { name: 'Rural Development and Technology', url: 'https://crdt.iitd.ac.in/' },
    { name: 'Sensors, INstrumentation and Cyber-physical Systems Engineering', url: 'https://sense.iitd.ac.in/' },
    { name: 'Value Education in Engineering', url: 'http://nrcvee.iitd.ac.in/' },
    { name: 'Optics and Photonics', url: 'https://opc.iitd.ac.in/index.html' }
  ];

  const schools = [
    { name: 'Biological Sciences', url: 'https://bioschool.iitd.ac.in/' },
    { name: 'Information Technology', url: 'https://sit.iitd.ac.in/' },
    { name: 'Interdisciplinary Research', url: 'https://sire.iitd.ac.in/' },
    { name: 'Public Policy', url: 'https://spp.iitd.ac.in/' },
    { name: 'Telecommunication Technology and Management', url: 'https://bhartischool.iitd.ac.in/' },
    { name: 'Artificial Intelligence', url: 'https://scai.iitd.ac.in/' }
  ];

  const getActiveData = () => {
    switch(activeTab) {
      case 'departments': return departments;
      case 'centers': return centers;
      case 'schools': return schools;
      default: return departments;
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'departments': return <BookOpen className="w-6 h-6" />;
      case 'centers': return <Building className="w-6 h-6" />;
      case 'schools': return <GraduationCap className="w-6 h-6" />;
      default: return <BookOpen className="w-6 h-6" />;
    }
  };

  const filteredData = getActiveData().filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabConfigs = [
    { id: 'departments', label: 'Departments', icon: BookOpen, count: departments.length },
    { id: 'centers', label: 'Centers', icon: Building, count: centers.length },
    { id: 'schools', label: 'Schools', icon: GraduationCap, count: schools.length }
  ];

  return (
    <section id="departments" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse delay-1000"></div>
      <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse delay-2000"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Users className="w-4 h-4" />
            Academic Excellence
          </div>
          <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-red-600 via-red-700 to-red-800 bg-clip-text text-transparent mb-6">
            Academic Divisions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            IIT Delhi provides science-based engineering education with a view to produce quality engineer-scientists. 
            The curriculum provides broad-based knowledge and builds a temper for lifelong learning and exploring.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {tabConfigs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 min-w-[140px] ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white shadow-xl scale-105 shadow-red-200'
                    : 'bg-white text-gray-700 hover:bg-red-50 hover:text-red-600 hover:scale-105 shadow-md'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  activeTab === tab.id 
                    ? 'bg-red-700 text-white' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-red-100 group-hover:text-red-600'
                }`}>
                  {tab.count}
                </span>
                {activeTab === tab.id && (
                  <div className="absolute inset-0 rounded-xl bg-red-600 opacity-20 animate-pulse"></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-300 bg-white/80 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredData.map((item, index) => (
            <a
              key={index}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2 border border-red-100 overflow-hidden"
            >
              {/* Background gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-100 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-red-100 rounded-xl group-hover:bg-red-200 transition-colors duration-300">
                    {getIcon(activeTab)}
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors duration-300" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-red-700 transition-colors duration-300 leading-tight mb-2">
                  {item.name}
                </h3>
                
                <div className="flex items-center text-red-600 font-medium text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <span>Learn More</span>
                  <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>

              {/* Hover effect border */}
              <div className="absolute inset-0 rounded-2xl border-2 border-red-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
          ))}
        </div>

        {/* No results message */}
        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Filter className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-xl">No {activeTab} found matching "{searchTerm}"</p>
              <p className="text-gray-500">Try adjusting your search terms</p>
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className="mt-16 bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-bold text-red-600">{departments.length}</div>
              <div className="text-gray-600 font-medium">Academic Departments</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-red-600">{centers.length}</div>
              <div className="text-gray-600 font-medium">Centers</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-red-600">{schools.length}</div>
              <div className="text-gray-600 font-medium">Specialized Schools</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DepartmentsSection;